import {
  addDoc as originalAddDoc,
  setDoc as originalSetDoc,
  updateDoc as originalUpdateDoc,
  deleteDoc as originalDeleteDoc
} from 'firebase/firestore';
import { logAction } from './AuditLogger';

const collectionTranslations = {
  'user': 'Clientes',
  'requests': 'Pedidos/Vendas',
  'outgoing': 'Despesas',
  'stock': 'Estoque',
  'dailyStockSnapshot': 'Fechamento Diário de Estoque',
  'sideDishes': 'Acompanhamentos',
  'item': 'Pratos',
  'button': 'Categorias',
  'PictureMode': 'Configuração Visual',
  'taxDocuments': 'Notas Fiscais',
  'recipes': 'Receitas',
  'supplies': 'Insumos',
  'admins': 'Administradores',
  'audit_logs': 'Logs de Auditoria',
  'promotions': 'Promoções'
};

const recentLogs = {};
const DEBOUNCE_TIME_MS = 2000;

/**
 * Traduz o nome da coleção para PT-BR
 */
const translateCollection = (collName) => {
  return collectionTranslations[collName] || collName;
};

/**
 * Traduz os dados do objeto modificado em uma frase humana curta.
 */
const summarizeData = (actionVerb, collNameTranslated, data) => {
  if (!data) return `${actionVerb} um registro na área de ${collNameTranslated}.`;
  
  // Farejador de Identidade Avançado
  const identifier = (data.fantasyName && data.fantasyName !== 'anonimo' ? data.fantasyName : null) ||
                     (data.name && data.name !== 'anonimo' && data.name !== 'anonymous' ? data.name : null) ||
                     data.title || data.nome || data.titulo || data.descricao || 
                     data.productName || data.product || data.expenseName || data.supplierName || 
                     data.clientName || data.customerName || data.categoryName || data.ItemName || data.itemName;

  if (identifier) {
    return `${actionVerb} '${identifier}' na área de ${collNameTranslated}.`;
  }
  
  // Se não achou um nome humano legível (ex: atualizou apenas {quantidade: 5}), 
  // ignora IDs encriptados para manter o log limpo.
  return `${actionVerb} um registro na área de ${collNameTranslated}.`;
};

/**
 * Extrai o nome real da coleção.
 */
const extractCollectionName = (ref) => {
  if (!ref) return 'desconhecida';
  if (ref.parent && ref.parent.path) {
    return ref.parent.path.split('/')[0] || ref.path.split('/')[0];
  }
  if (ref.path) {
    return ref.path.split('/')[0];
  }
  return 'desconhecida';
};

/**
 * Escudo Anti-Flood: Evita gravar dezenas de logs repetidos no mesmo segundo.
 */
const shouldLog = (actionVerb, collName) => {
  const cacheKey = `${actionVerb}_${collName}`;
  const now = Date.now();
  if (recentLogs[cacheKey] && (now - recentLogs[cacheKey] < DEBOUNCE_TIME_MS)) {
    // É uma operação em massa (mesma ação na mesma coleção em < 2 segundos)
    return false;
  }
  recentLogs[cacheKey] = now;
  return true;
};

const processAuditLog = async (actionVerb, ref, data = null) => {
  const collName = extractCollectionName(ref);
  
  // Exceções
  if (collName === 'audit_logs' || collName === 'admins') return;
  
  if (!shouldLog(actionVerb, collName)) return;

  const collNameTranslated = translateCollection(collName);
  const summary = summarizeData(actionVerb, collNameTranslated, data);
  
  await Promise.resolve(logAction(summary, '')).catch(console.error);
};

const sanitizeData = (data) => {
  if (data === undefined || data === null) return data;
  if (Array.isArray(data)) {
    return data
      .filter((item) => item !== undefined)
      .map((item) => (typeof item === 'object' && item !== null ? sanitizeData(item) : item));
  }
  if (typeof data === 'object') {
    if (data.constructor && data.constructor.name !== 'Object') {
      return data;
    }
    const result = {};
    for (const [key, val] of Object.entries(data)) {
      if (val !== undefined) {
        result[key] = typeof val === 'object' && val !== null ? sanitizeData(val) : val;
      }
    }
    return result;
  }
  return data;
};

export const addDoc = async (collRef, data) => {
  const clean = sanitizeData(data);
  await processAuditLog('Criou', collRef, clean);
  return originalAddDoc(collRef, clean);
};

export const setDoc = async (docRef, data, options) => {
  const clean = sanitizeData(data);
  const verb = (options && options.merge) ? 'Atualizou' : 'Definiu';
  await processAuditLog(verb, docRef, clean);
  return originalSetDoc(docRef, clean, options);
};

export const updateDoc = async (docRef, data) => {
  const clean = sanitizeData(data);
  await processAuditLog('Atualizou', docRef, clean);
  return originalUpdateDoc(docRef, clean);
};

export const deleteDoc = async (docRef) => {
  await processAuditLog('Excluiu', docRef, null);
  return originalDeleteDoc(docRef);
};
